import { api, apiFetch } from '../api/client';

/**
 * The spoken welcome.
 *
 * A recording, served by the API and played on the catalogue. Nothing is
 * synthesised. There used to be a fallback that read the same words with the
 * browser's own speech, and it was taken out deliberately: a machine voice
 * standing in for a recording somebody chose is not graceful degradation, it
 * is a different product speaking, and silence is the better failure.
 *
 * The one thing not in our gift is autoplay. A browser will not make noise at
 * a page nobody has touched; that is a rule, not a setting. So a greeting the
 * browser refuses waits for the first click, tap or key, and plays then.
 */

/** Remembered per device, like the theme. */
export const WELCOME_VOICE_KEY = 'sx.voice.welcome';

export function isWelcomeVoiceOn(): boolean {
  try {
    // On by default: it was asked for. The switch exists to turn it off.
    return window.localStorage.getItem(WELCOME_VOICE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setWelcomeVoiceOn(on: boolean): void {
  try {
    window.localStorage.setItem(WELCOME_VOICE_KEY, on ? 'on' : 'off');
  } catch {
    /* private mode: the choice just will not survive a reload */
  }
}

/** Which of the three greetings the server holds. */
export function greetingPart(hour = new Date().getHours()): 'morning' | 'afternoon' | 'evening' {
  return hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
}

export interface ServerVoice {
  enabled: boolean;
  /** The file the greeting is served from. */
  voice: string;
}

export async function serverVoiceStatus(): Promise<ServerVoice | null> {
  try {
    const status = await api<ServerVoice>('/voice/status');
    return status.enabled ? status : null;
  } catch {
    // An older server has no such route. That is not an error worth showing.
    return null;
  }
}

export interface PlayedGreeting {
  voice: string;
  /**
   * True when the browser refused to start it until the visitor interacts.
   *
   * The greeting is not lost in that case — it is armed, and the next click,
   * key or touch plays it.
   */
  deferred: boolean;
  /** Stop playing, or stop waiting to play. */
  stop: () => void;
  /**
   * Resolves when the recording has finished — or when it has failed, or been
   * stopped. Never rejects.
   *
   * The catalogue sequences three things on this: the assistant's portrait is
   * shown, she speaks, and only when she has finished does the chat panel
   * take her place. Without a completion signal that ordering would have to
   * be guessed from a hard-coded duration, which is wrong the moment the
   * recording is replaced.
   */
  finished: Promise<void>;
  /** Resolves when sound actually starts — at once unless `deferred`. */
  started: Promise<void>;
  /**
   * Try to start a deferred greeting now. Call it from inside a click or key
   * handler; resolves true if it is playing.
   */
  start: () => Promise<boolean>;
}

/** One object URL per greeting, reused for the life of the page. */
let cached: { part: string; url: string } | null = null;

/**
 * Whatever is currently playing.
 *
 * Two things can ask for the greeting in quick succession — React mounts an
 * effect twice in development, and the Test button can be pressed while one
 * is still playing. Without this they overlap and the voice talks over
 * itself.
 */
let speaking: HTMLAudioElement | null = null;

/** Stop anything already playing. */
export function stopGreeting(): void {
  if (!speaking) return;
  speaking.pause();
  speaking.currentTime = 0;
  speaking = null;
}

/**
 * Fetch and play the greeting. Resolves null when the server has none.
 *
 * Played through an `<audio>` element rather than a Web Audio gain stage: the
 * recording arrives at the level it was made at, and a gain above 1 would
 * only clip it.
 */
export async function playServerGreeting(
  hour = new Date().getHours(),
  {
    /**
     * Never start by itself: load the recording and wait for `start()`,
     * called from the visitor's tap. The catalogue welcome works this way by
     * request — it plays when someone asks to hear it, not on arrival.
     */
    waitForTap = false,
  }: { waitForTap?: boolean } = {},
): Promise<PlayedGreeting | null> {
  const part = greetingPart(hour);
  try {
    let url = cached?.part === part ? cached.url : null;
    let voice = '';

    if (!url) {
      const response = await apiFetch(`/voice/welcome/${part}`, {
        headers: { Accept: 'audio/*' },
      });
      if (!response.ok) return null;
      voice = response.headers.get('X-Voice-Name') ?? '';
      url = URL.createObjectURL(await response.blob());
      if (cached) URL.revokeObjectURL(cached.url);
      cached = { part, url };
    }

    stopGreeting();
    const audio = new Audio(url);
    audio.volume = 1;
    speaking = audio;

    // Settled rather than resolved-on-success: a recording that errors, or
    // one the visitor stops, has still finished as far as the caller is
    // concerned, and anything waiting on it must not wait for ever.
    let done!: () => void;
    const finished = new Promise<void>((resolve) => {
      done = resolve;
    });
    const settle = () => {
      if (speaking === audio) speaking = null;
      done();
    };
    audio.addEventListener('ended', settle);
    audio.addEventListener('error', settle);
    audio.addEventListener('pause', () => {
      // Pausing at the end is the 'ended' case; pausing short of it is a stop.
      if (audio.currentTime === 0 || audio.ended) settle();
    });

    if (!waitForTap) {
      try {
        await audio.play();
        return {
          voice,
          deferred: false,
          stop: stopGreeting,
          finished,
          started: Promise.resolve(),
          start: () => Promise.resolve(true),
        };
      } catch (error) {
        // Autoplay refused — the normal case on a first visit, on every
        // browser, phones included. Wait for the visitor's first interaction.
        if ((error as DOMException)?.name !== 'NotAllowedError') return null;
      }
    }

    // From here the greeting is armed but silent, waiting to be started:
    // either autoplay was refused, or the caller asked for a tap (`start`).

    let begin!: () => void;
    const started = new Promise<void>((resolve) => {
      begin = resolve;
    });

    // Only events a browser counts as permission to play sound. A touch
    // grants it when the finger lifts (touchend / click), not when it lands
    // — that is so scrolling does not count — which is why the earlier
    // pointerdown/touchstart version never played on a phone: the attempt
    // was refused, the one-shot listener was gone, and she waited for ever.
    const events: (keyof WindowEventMap)[] = ['click', 'touchend', 'pointerup', 'keydown'];
    let attempting = false;
    const detach = () => {
      for (const event of events) window.removeEventListener(event, fire, true);
    };
    // Synchronous from the event handler: Safari only honours a play()
    // made inside the gesture itself, not one made after an await.
    const start = (): Promise<boolean> => {
      if (attempting) return Promise.resolve(false);
      attempting = true;
      return audio.play().then(
        () => {
          detach();
          begin();
          return true;
        },
        () => {
          // Not a gesture the browser accepted (a scroll, an Escape).
          // Keep listening; the next one may be.
          attempting = false;
          return false;
        },
      );
    };
    function fire(event: Event) {
      // "Skip" is a refusal, not a go-ahead: starting the sound on the way
      // to stopping it would be a blip of voice for someone who said no.
      if ((event.target as Element | null)?.closest?.('[data-welcome-skip]')) return;
      void start();
    }
    // Capture phase, so a control that stops propagation still counts.
    // Not when the caller asked for a tap: then only `start` plays it.
    if (!waitForTap) {
      for (const event of events) {
        window.addEventListener(event, fire, { capture: true, passive: true });
      }
    }

    return {
      voice,
      deferred: true,
      finished,
      started,
      start,
      stop: () => {
        detach();
        stopGreeting();
        settle();
      },
    };
  } catch {
    // No recording, offline, an older server — all the same answer here.
    return null;
  }
}
